import type { WordDiffSegment } from "@/utils/word-diff";

export interface VariantBDiffTextProps {
  segments: WordDiffSegment[];
}

/**
 * Draws a `diffWords` result in the design's markup
 * (`design-input/strategy-tab-and-ab-test-creation/prototype-extracted.md § 4.2`,
 * `VariantBTitleMarkup.tsx:147-166` reused for bullets). The switch, the
 * field editor's read/edit modes and `No changes yet` are `WEB-003`'s.
 */
/**
 * A `removed`/`added` segment that is pure whitespace (no word content)
 * carries no meaningful change to show -- it exists only because `diffWords`
 * matched a word whose trailing whitespace differed between a and b
 * (word-diff.ts:96-107). The design highlights change over real word content
 * only (`prototype-extracted.md § 4.2`; the reference markup's `isBlank`
 * check), so a whitespace-only segment renders plain instead of a coloured,
 * struck-through rectangle over nothing but a space. It still renders --
 * dropping it would break the reconstruction of b's text -- it is just not
 * highlighted.
 */
const isWhitespaceOnly = (text: string): boolean => text.length > 0 && text.trim() === "";

export function VariantBDiffText({ segments }: VariantBDiffTextProps) {
  return (
    <p className="text-base leading-relaxed text-foreground">
      {segments.map((segment, index) => {
        if (segment.type === "removed" && !isWhitespaceOnly(segment.text)) {
          return (
            <span
              key={index}
              className="rounded-sm bg-status-error-soft text-foreground line-through decoration-1"
            >
              {segment.text}
            </span>
          );
        }
        if (segment.type === "added" && !isWhitespaceOnly(segment.text)) {
          return (
            <span key={index} className="rounded-sm bg-status-success-soft text-foreground">
              {segment.text}
            </span>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </p>
  );
}
